/**
 * SurgeSkillEffect — Specter wraith-path Endurance branch: instant stamina refill.
 *
 * Instantly refills the stamina bar to full. The panic button when you've been
 * sprinting and need your dash charges back. Doesn't break stealth. Medium
 * cooldown tier. Fizzles until mastered (freshness gate).
 *
 * Upgrade hooks (via resolver, scope 'surge'):
 * - Deep Reserves: `staminaMaxBonus` (+30% max stamina, always on) and
 *   `zeroDrainWindowMs` (temporary zero drain after activation)
 * - Perpetual Motion: `dashRefillRequiresFullStamina` (always on, handled
 *   by PerpetualMotionSkillEffect directly on DashComponent)
 *
 * When Deep Reserves is equipped, activation also grants a temporary window
 * of zero stamina drain (sets drainMultiplier to 0, restores after window).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Surge
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SurgeDeps {
  readonly targeting: {
    /** Returns true if the evolution freshness signal should reduce effectiveness. */
    shouldFizzle(): boolean;
  };
  readonly stamina: {
    /** Current stamina value. */
    current: number;
    /** Effective maximum stamina (including bonuses). */
    readonly max: number;
    /** Multiplier applied to stamina drain rate. Set to 0 for zero-drain window. */
    drainMultiplier: number;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SurgeConfig {
  readonly cooldownMs: number;
}

const DEFAULT_CONFIG: SurgeConfig = {
  cooldownMs: 1200,              // medium cooldown tier
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SurgeSkillEffect implements SkillEffect {
  readonly skillId = 'surge';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;    // utility skill — doesn't spend stamina
  readonly failLabel = 'FIZZLE';

  private readonly deps: SurgeDeps;
  private readonly config: SurgeConfig;

  private _cooldownRemaining = 0;
  private _fizzled = false;

  /** Tracks the zero-drain window granted by Deep Reserves. */
  private _zeroDrainRemaining = 0;
  /** Saved drainMultiplier to restore after the zero-drain window expires. */
  private _savedDrainMultiplier = 1.0;

  constructor(deps: SurgeDeps, config: Partial<SurgeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get cooldownDuration(): number { return this.config.cooldownMs; }
  get fizzled(): boolean { return this._fizzled; }
  get zeroDrainRemaining(): number { return this._zeroDrainRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — consumes cooldown but does not refill stamina
    this._fizzled = this.deps.targeting.shouldFizzle();

    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Refill stamina to full
    this.deps.stamina.current = this.deps.stamina.max;

    // Deep Reserves: temporary zero-drain window after activation
    const zeroDrainWindowMs = this.deps.resolver.getAdditiveEffect(
      'surge', 'zeroDrainWindowMs',
    );
    if (zeroDrainWindowMs > 0) {
      // Only save the original multiplier if we're not already in a zero-drain
      // window — re-activating mid-window should extend it, not overwrite the
      // saved value with the suppressed 0.
      if (this._zeroDrainRemaining <= 0) {
        this._savedDrainMultiplier = this.deps.stamina.drainMultiplier;
      }
      this.deps.stamina.drainMultiplier = 0;
      this._zeroDrainRemaining = zeroDrainWindowMs;
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick zero-drain window
    if (this._zeroDrainRemaining > 0) {
      this._zeroDrainRemaining = Math.max(0, this._zeroDrainRemaining - delta);
      if (this._zeroDrainRemaining <= 0) {
        // Restore original drain multiplier
        this.deps.stamina.drainMultiplier = this._savedDrainMultiplier;
      }
    }
  }

  destroy(): void {
    // If zero-drain window is active, restore multiplier before cleanup
    if (this._zeroDrainRemaining > 0) {
      this.deps.stamina.drainMultiplier = this._savedDrainMultiplier;
      this._zeroDrainRemaining = 0;
    }
    this._cooldownRemaining = 0;
  }
}
